/* eslint-disable import/no-unused-modules */
import _ from 'lodash';
import JSCookie from 'js-cookie';
import { Socket } from 'phoenix';
import Logger from './Logger';

const BACKOFF_BASE_MS = 5;
const BACKOFF_MAX_MS = 2 * 60 * 1000;
const BACKOFF_FACTOR = 2;

const BACKOFF_INTERVALS: number[] = [];
for (
  let i = BACKOFF_FACTOR;
  i * BACKOFF_BASE_MS < BACKOFF_MAX_MS;
  i *= BACKOFF_FACTOR
) {
  BACKOFF_INTERVALS.push(Math.floor(BACKOFF_BASE_MS * i));
}

const reconnectBackoff = (tries: number) =>
  // Phoenix: [10, 50, 100, 150, 200, 250, 500, 1000, 2000] || 5000
  // Ours: [10, 20, 40, 80, 160, 320, 640, 1280, 2560, 5120, 10240, 20480, 40960, 81920] || 120000
  _.random(0, BACKOFF_INTERVALS[tries] || BACKOFF_MAX_MS);

const ENDPOINT_PATH = '/conn';
const GOOGLE_LOAD_BALANCER_SESSION_AFFINITY_HEADER = 'GCLB';
const MAX_FIRST_CONNECT_MS = 10 * 60 * 1000;
const WS_NORMAL_CLOSURE = 1000;

class SlabSocket extends Socket {
  public authToken: string;

  private clientDisconnecting = false;
  private maxFirstConnectTimer: ReturnType<typeof setTimeout> | null;
  private numCloses = 0;
  private numOpens = 0;

  constructor(authToken: string) {
    super(ENDPOINT_PATH, {
      timeout: 30 * 1000,
      reconnectAfterMs: reconnectBackoff,
      // Workaround to create WebSocket with subprotocol header
      // @ts-ignore
      transport: function transport(url: string) {
        return new WebSocket(url, ['auth.slab.ws', authToken]);
      },
    });
    this.authToken = authToken;

    this.onOpen(() => {
      this.numOpens += 1;
      if (this.maxFirstConnectTimer != null) {
        clearTimeout(this.maxFirstConnectTimer);
        this.maxFirstConnectTimer = null;
      }
    });
    this.onClose((event) => {
      this.countClose();
      // Phoenix already automatically reconnects abnormal closures
      if (!this.clientDisconnecting && event.code === WS_NORMAL_CLOSURE) {
        this.scheduleReconnect();
      }
    });
    window.addEventListener('online', () => this.scheduleReconnect());

    this.maxFirstConnectTimer = setTimeout(() => {
      Logger.warn('Gave up Socket connect', { endpoint: this.endPointURL() });
      this.disconnect();
    }, MAX_FIRST_CONNECT_MS);
  }

  countClose() {
    this.numCloses += 1;
    if (this.numCloses >= 100 && this.numCloses % 10 === 0) {
      Logger.warn('High Socket close count', {
        numCloses: this.numCloses,
        numOpens: this.numOpens,
        endpoint: this.endPointURL(),
      });
    }
  }

  disconnect(
    callback?: () => void | Promise<void>,
    code?: number,
    reason?: string,
  ): void {
    this.clientDisconnecting = true;
    super.disconnect(callback, code, reason);
  }

  scheduleReconnect() {
    if (this.isConnected()) return;
    // Don't try same server
    JSCookie.remove(GOOGLE_LOAD_BALANCER_SESSION_AFFINITY_HEADER);
    // @ts-expect-error
    this.reconnectTimer.reset();
    // @ts-expect-error
    this.reconnectTimer.scheduleTimeout();
  }
}

let socket: SlabSocket;
const createOrGetSocket = (authToken: string): SlabSocket => {
  if (socket == null) {
    socket = new SlabSocket(authToken);
  } else if (authToken !== socket.authToken) {
    // Logger.warn('Duplicate socket requested, returning existing');
  }
  return socket;
};

export { createOrGetSocket, SlabSocket };
